import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Control, FieldValues, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  ProjectExpenseData,
  ProjectPurchaseOrderExpensePayload,
} from "../../../../../../../api/services/project/model";
import { useCreateProjectInvoiceMutation } from "../../../../../../../api/services/project/projectApis";
import { ReactComponent as DownloadIcon } from "../../../../../../../assets/images/download.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiCheckbox,
  MuiDateFieldFormController,
  MuiLink,
  MuiModal,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
  RowProps,
  WarningConfirmationModal,
} from "../../../../../../../components";
import { ButtonWithIcon } from "../../../../../../../components/buttonWithIcon/ButtonWithIcon";
import { MuiDoubleLabelHorizontal } from "../../../../../../../components/doubleLabel/MuiDoubleLabelHorizontal";
import MuiEllipseLabel from "../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import PermissionWrapper from "../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import {
  CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
  DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
  DELIVERABLE_FRAMEWORK_TYPE,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../helpers/constants/countryDateFormat";
import {
  KeyValue,
  TableActions,
  TableActionType,
} from "../../../../../../../helpers/types/common";
import {
  buildActionMenuItems,
  checkWithLowerCase,
  convertToUserFormat,
  convertToUTCFormat,
  filterListByFilterKeyValue,
  getAmountWithCurrency,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../../redux/slices/appState/appStateSlice";
import { keyDetailsSelector } from "../../../../../../../redux/slices/project/projectSelector";
import { updateProjectOpcoFieldsEditable } from "../../../../../../../redux/slices/project/projectSlice";
import { useDownloadInvoiceToExcel } from "../../../../../../createProgram/hooks/useDownloadInvoiceToExcel";
import { isProjectFeeType } from "../../../../../helper";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  ProjectFeesAndExpenseRow,
  ProjectFeesAndExpensesColumnKey,
} from "../../../../../modelsAndSchemas/Models";
import {
  projectAddFeesAndExpensesSiteCoreKeys,
  projectFeesAndExpensesSiteCoreKeys,
  projectInvoiceHistorySiteCoreKeys,
  projectRaiseInvoiceSiteCoreKeys,
} from "../../../../../siteCore/SiteCoreKeys";
import { ProjectInvoiceHistory } from "../../../../invoiceHistory/ProjectInvoiceHistoryMain";
import { ViewProjectFeeModal } from "../../../../viewProjectFeeModal/ViewProjectFeeModal";
import {
  CaptureValuesParams,
  ProjectRaiseInvoice,
} from "./projectRaiseInvoice/ProjectRaiseInvoiceModal";
import ViewProjectFeeExpenseModal from "./viewProjectFeesAndExpense/ProjectViewFeeExpenseModal";

const pageId = `${PAGE_IDS.PROJECT}-fees-and-expense-list`;

const ProjectSearchFormControl = ({
  name,
  control,
  placeholder,
  requiredARIALabel,
}: {
  name: string;
  control: Control<FieldValues, any>;
  placeholder: string;
  requiredARIALabel: string;
}) => (
  <FormController control={control} name={name} key={name}>
    <MuiTextField
      id={`${pageId}-${name}`}
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      }
      maxLength={MAX_LENGTH_50}
      placeholder={placeholder}
      showCloseIcon
      shouldTrimSpaces={false}
      requiredARIALabel={requiredARIALabel}
    />
  </FormController>
);

interface ProjectFeesAndExpenseListProps {
  projectExpenseList: ProjectExpenseData[];
  isAddButtonVisible: boolean;
  handleAddFeesAndExpense: () => void;
  onActionClick: (
    selectedInvoice: ProjectExpenseData,
    actionName: TableActionType,
  ) => void;
  onRefetchApi: (projectId: string) => void;
  projectId: string;
  poNumber?: string;
  isViewOnly?: boolean;
  billingCurrencyName: string;
  isEditProject?: boolean;
  clientType?: string;
}

const ProjectSearchDropdownControl = ({
  name,
  list,
  control,
  placeholder,
  requiredARIALabel,
}: {
  name: string;
  list: OptionsDataType[];
  control: Control<FieldValues, any>;
  placeholder: string;
  requiredARIALabel: string;
}) => (
  <FormController control={control} name={name} key={name}>
    <MuiSelect
      label=""
      options={list}
      visibleDefaultSelectOption={true}
      value=""
      id={`${pageId}-${name}`}
      placeholder={placeholder}
      requiredARIALabel={requiredARIALabel}
    />
  </FormController>
);

export const ProjectFeesAndExpenseList = ({
  onActionClick,
  projectExpenseList,
  isAddButtonVisible,
  handleAddFeesAndExpense,
  onRefetchApi,
  projectId,
  isViewOnly = false,
  billingCurrencyName,
  poNumber,
  isEditProject,
  clientType,
}: ProjectFeesAndExpenseListProps) => {
  const {
    useFetchLabel,
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const projectFeeAndExpenseTableRef = useRef<{ resetPagination: () => void }>(
    null,
  );
  const getProjectFeeAndExpenseLabel = useFetchLabel(
    projectFeesAndExpensesSiteCoreKeys.feesAndExpenses,
  );
  const { control } = useForm<Partial<KeyValue>>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const isSiteCoreApiLoading = useSelector(siteCoreContentLoadingSelector);
  const keyDetails = useSelector(keyDetailsSelector);
  const [projectFeeAndExpenseState, setProjectFeeAndExpenseState] = useState<{
    filteredFeeAndExpenseList: ProjectFeesAndExpenseRow[];
    originalFeeAndExpenseList: ProjectFeesAndExpenseRow[];
    categoryOptions: OptionsDataType[];
    feeOrExpenseTypeOptions: OptionsDataType[];
    typeIdOptions: OptionsDataType[];
    totalBillingAmount: number;
    totalTax: number;
  }>({
    filteredFeeAndExpenseList: [],
    originalFeeAndExpenseList: [],
    categoryOptions: [],
    typeIdOptions: [],
    feeOrExpenseTypeOptions: [],
    totalBillingAmount: 0,
    totalTax: 0,
  });
  const [showDossierError, setShowDossierError] = useState(false);
  const [showProjectRaiseInvoiceModal, setShowProjectRaiseInvoiceModal] =
    useState(false);
  const [showInvoiceHistoryModal, setShowInvoiceHistoryModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [projectInvoiceNumber, setProjectInvoiceNumber] = useState<string>("");
  const [projectInvoiceId, setProjectInvoiceId] = useState<string>("");
  const [selectedPONumber, setSelectedPONumber] = useState<string>("");
  const [projectInvoiceDate, setProjectInvoiceDate] = useState<string>("");
  const [paymentTerms, setPaymentTerms] = useState<string>("");
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState<string>("");
  const isEditWithPermission =
    !isViewOnly &&
    hasModulePermission(MODULE_PERMISSIONS.PROGRAM_ADD_INVOICE_GENERATION);

  const dispatch = useDispatch();

  const {
    mutate: projectRaiseInvoice,
    isPending: isProjectRaiseInvoiceLoading,
    data: projectRaiseInvoiceData,
    isError: isProjectRaiseInvoiceError,
    error: projectRaiseInvoiceError,
  } = useCreateProjectInvoiceMutation();

  const isClientTypeNCBP = useMemo(
    () =>
      checkWithLowerCase(
        clientType,
        CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
      ),
    [clientType],
  );

  useEffect(() => {
    if (projectRaiseInvoiceData) {
      setShowConfirmationModal(false);
      setProjectInvoiceNumber(projectRaiseInvoiceData.number);
      setProjectInvoiceId(projectRaiseInvoiceData.id);
      setShowSuccessModal(true);

      if (isClientTypeNCBP) {
        dispatch(updateProjectOpcoFieldsEditable(false));
      }
    }
  }, [projectRaiseInvoiceData]);

  useEffect(() => {
    if (isProjectRaiseInvoiceLoading) {
      dispatch(
        toggleLoader({
          isVisible: isProjectRaiseInvoiceLoading,
        }),
      );
      return () => {
        dispatch(
          toggleLoader({
            isVisible: false,
          }),
        );
      };
    }
  }, [isProjectRaiseInvoiceLoading]);

  useEffect(() => {
    if (isProjectRaiseInvoiceError) {
      setShowConfirmationModal(false);
    }
  }, [isProjectRaiseInvoiceError]);

  // When user clicks primary button in ProjectRaiseInvoice, show confirmation modal
  const handlePrimaryButtonClick = () => {
    setShowConfirmationModal(true); // Open confirmation modal
  };

  const handleConfirm = () => {
    const checkedItems =
      projectFeeAndExpenseState.filteredFeeAndExpenseList.filter(
        (item) => item.isChecked,
      );
    const payload: ProjectPurchaseOrderExpensePayload = {
      localInvoiceNumber,
      projectExpenses: checkedItems.map((item, index) => {
        return {
          expenseId: item.id,
          lineNumber: index + 1,
        };
      }),
      purchaseOrderId: selectedPONumber ?? "",
      feeOrExpenseAmount: parseFloat(
        (
          projectFeeAndExpenseState.totalBillingAmount -
          projectFeeAndExpenseState.totalTax
        ).toFixed(2),
      ),
      totalTaxAmount: parseFloat(projectFeeAndExpenseState.totalTax.toFixed(2)),
      totalAmount: parseFloat(
        projectFeeAndExpenseState.totalBillingAmount.toFixed(2),
      ),
      invoiceDate: convertToUTCFormat(
        projectInvoiceDate,
        userCountryDateFormat,
      ),
      paymentTermId: paymentTerms,
    };

    projectRaiseInvoice({
      projectId: projectId,
      payload: payload,
    });
  };

  const handleInvoiceButtonClick = () => {
    setShowInvoiceHistoryModal(false);
    onRefetchApi(projectId); // This triggers the parent API refetch
  };
  const handleSuccessClose = () => {
    onRefetchApi(projectId);
    setShowProjectRaiseInvoiceModal(false);
    setShowSuccessModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };
  const [showProjectFeeViewModal, setShowProjectFeeViewModal] = useState(false);
  const [showViewFeesAndExpenseModal, setShowViewFeesAndExpenseModal] =
    useState(false);

  const [selectedProjectExpenseId, setSelectedProjectExpenseId] =
    useState<string>("");

  const handleOnViewClick = (row: ProjectFeesAndExpenseRow) => {
    switch (row?.typeId) {
      case ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_FEE: {
        setShowProjectFeeViewModal(true);
        break;
      }
      case ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE:
      case ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE: {
        setSelectedProjectExpenseId(row.id);
        setShowViewFeesAndExpenseModal(true);
        break;
      }
    }
  };

  const getAddFeesExpenseLabel = (key: string) => {
    return fetchLabel(
      projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
      key,
    );
  };

  const getProjectFeeLabel = () => {
    return getAddFeesExpenseLabel(
      projectAddFeesAndExpensesSiteCoreKeys.projectFeeType,
    );
  };

  const getStatusValueByKeyOfDropdown = (statusId: string) => {
    return fetchValueByKeyOfDropdown(
      projectFeesAndExpensesSiteCoreKeys.feesAndExpenses,
      projectFeesAndExpensesSiteCoreKeys.itemStatusList,
      statusId,
    );
  };

  // extract: check if expense should be marked checked
  const isExpenseChecked = (
    expense: ProjectExpenseData,
    prevCheckedList: ProjectFeesAndExpenseRow[],
  ) => {
    if (expense.statusId !== STATUS_BADGE_TYPE.unBilled) return false;
    return !!prevCheckedList.find((item) => item.id === expense.id)?.isChecked;
  };

  const expenseCollectOptions = (
    expense: ProjectExpenseData,
    categoryOptions: Set<string>,
    typeOptions: Set<string>,
    feeOrExpenseTypeOptions: Set<string>,
  ) => {
    if (expense["expenseCategoryId"]?.name) {
      categoryOptions.add(expense["expenseCategoryId"].name);
    }
    if (expense?.expenseTypeId?.name || expense["feeType"]?.name) {
      feeOrExpenseTypeOptions.add(
        expense["expenseTypeId"]?.name ?? expense["feeType"]?.name ?? "",
      );
    }
    if (expense?.typeId) {
      typeOptions.add(getAddFeesExpenseLabel(expense["typeId"]));
    }
  };

  const buildProjectExpenseRow = (
    expense: ProjectExpenseData,
    prevCheckedList: ProjectFeesAndExpenseRow[],
  ) => ({
    ...expense,
    status: getStatusValueByKeyOfDropdown(expense.statusId),
    transactionDate: convertToUserFormat(
      expense.transactionDate,
      userCountryDateFormat,
    ),
    typeName: getAddFeesExpenseLabel(expense.typeId),
    name: isProjectFeeType(expense.typeId)
      ? getProjectFeeLabel()
      : expense?.name || "",
    isChecked: isExpenseChecked(expense, prevCheckedList),
    expenseCategoryName: expense["expenseCategoryId"]?.name,
    currencyName: expense["currency"]?.name,
    feeOrExpenseType:
      (expense["expenseTypeId"]?.name || expense["feeType"]?.name) ?? "",
    lineNumber: "",
  });

  // extract: map set → { label, value } array
  const toOptionArray = (set: Set<string>) =>
    [...set].map((value) => ({ label: value, value }));

  const mapFeesAndExpensesData = (data: ProjectExpenseData[]) => {
    const response: ProjectFeesAndExpenseRow[] = [];
    const categoryOptions = new Set<string>();
    const typeOptions = new Set<string>();
    const feeOrExpenseTypeOptions = new Set<string>();
    const prevCheckedList =
      projectFeeAndExpenseState.originalFeeAndExpenseList.filter(
        (item) =>
          item?.isChecked && item.statusId === STATUS_BADGE_TYPE.unBilled,
      );
    data.forEach((expense: ProjectExpenseData) => {
      expenseCollectOptions(
        expense,
        categoryOptions,
        typeOptions,
        feeOrExpenseTypeOptions,
      );

      response.push(buildProjectExpenseRow(expense, prevCheckedList));
    });

    const totalBillingAmount =
      projectFeeAndExpenseState.originalFeeAndExpenseList.length > 0
        ? getTotalSelectedAmount(response, "totalAmount")
        : 0;

    const totalTaxAmount = getTotalSelectedAmount(response, "tax");
    setProjectFeeAndExpenseState((prevState) => ({
      ...prevState,
      totalBillingAmount,
      filteredFeeAndExpenseList: response,
      originalFeeAndExpenseList: response,
      categoryOptions: toOptionArray(categoryOptions),
      feeOrExpenseTypeOptions: toOptionArray(feeOrExpenseTypeOptions),
      typeIdOptions: toOptionArray(typeOptions),
      totalTax: totalTaxAmount,
    }));
  };

  useEffect(() => {
    if (projectExpenseList?.length && !isSiteCoreApiLoading) {
      mapFeesAndExpensesData(projectExpenseList);
    } else {
      setProjectFeeAndExpenseState((prevState) => ({
        ...prevState,
        filteredFeeAndExpenseList: [],
        originalFeeAndExpenseList: [],
      }));
    }
  }, [projectExpenseList, isSiteCoreApiLoading]);

  const getTotalProjectInvoiceAmount = () => {
    if (isEmpty(projectExpenseList)) {
      return `${billingCurrencyName} ${DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL}`;
    }
    const billedList = projectExpenseList.filter(
      (item) => item.statusId === STATUS_BADGE_TYPE.billed,
    );
    const invoiceAmount = billedList.reduce((total, item) => {
      return total + (item.totalAmount || 0);
    }, 0);

    return `${billingCurrencyName} ${invoiceAmount.toFixed(2)}`;
  };

  const filters = useWatch({ control });

  useEffect(() => {
    appliedFilters(projectFeeAndExpenseState.originalFeeAndExpenseList);
  }, [filters]);

  const resetTablePagination = () => {
    if (projectFeeAndExpenseTableRef.current) {
      projectFeeAndExpenseTableRef.current.resetPagination();
    }
  };

  const appliedFilters = (ProgramExpenseData: ProjectFeesAndExpenseRow[]) => {
    if (!ProgramExpenseData.length) return;
    const filterList = filterListByFilterKeyValue(
      projectFeeAndExpenseState.originalFeeAndExpenseList,
      filters,
    );
    setProjectFeeAndExpenseState((prevState) => ({
      ...prevState,
      filteredFeeAndExpenseList:
        filterList as unknown as ProjectFeesAndExpenseRow[],
    }));
    resetTablePagination();
  };

  const emptyMessageLabel = getProjectFeeAndExpenseLabel(
    projectFeesAndExpensesSiteCoreKeys.noDataMessage,
  );

  const isAllSelected = useMemo(() => {
    const unBilledList =
      projectFeeAndExpenseState.originalFeeAndExpenseList?.filter(
        (item) => item.statusId === STATUS_BADGE_TYPE.unBilled,
      );
    if (unBilledList.length === 0) return false;
    return unBilledList.every((item) => item.isChecked);
  }, [projectFeeAndExpenseState.originalFeeAndExpenseList]);

  const onChangeSelectAll = (checked: boolean) => {
    let totalBillingAmount = 0;
    const updatedList = projectFeeAndExpenseState.filteredFeeAndExpenseList.map(
      (item) => {
        return {
          ...item,
          isChecked:
            item?.statusId === STATUS_BADGE_TYPE.unBilled ? checked : false,
        };
      },
    );

    const updatedOriginalList =
      projectFeeAndExpenseState.originalFeeAndExpenseList.map((item) => ({
        ...item,
        isChecked:
          item?.statusId === STATUS_BADGE_TYPE.unBilled ? checked : false,
      }));

    totalBillingAmount = getTotalSelectedAmount(updatedList, "totalAmount");
    const totalTaxAmount = getTotalSelectedAmount(updatedList, "tax");

    setProjectFeeAndExpenseState((prevState) => ({
      ...prevState,
      originalFeeAndExpenseList: updatedOriginalList,
      filteredFeeAndExpenseList: updatedList,
      totalBillingAmount,
      totalTax: totalTaxAmount,
    }));
  };

  const projectRaiseInvoiceLabel = useMemo(() => {
    const siteCoreLabel = getProjectFeeAndExpenseLabel(
      projectFeesAndExpensesSiteCoreKeys.raiseInvoiceBtn,
    );
    return replaceHashWithValue(siteCoreLabel, {
      TOTALINVOICEAMOUNT: `${billingCurrencyName} ${projectFeeAndExpenseState.totalBillingAmount.toFixed(2)}`,
    });
  }, [projectFeeAndExpenseState.totalBillingAmount]);

  const getTotalSelectedAmount = (
    listProjectFeesAndExpense: ProjectFeesAndExpenseRow[],
    key: keyof ProjectFeesAndExpenseRow,
  ) => {
    return listProjectFeesAndExpense.reduce((acc, item) => {
      if (item.isChecked) {
        return acc + ((item[key] as number) ?? 0);
      }
      return acc;
    }, 0);
  };

  const onChangeSelect = (checked: boolean, item: ProjectFeesAndExpenseRow) => {
    const filterListIndex =
      projectFeeAndExpenseState.filteredFeeAndExpenseList.findIndex(
        (i) => i.id === item.id,
      );
    const originalListIndex =
      projectFeeAndExpenseState.originalFeeAndExpenseList.findIndex(
        (i) => i.id === item.id,
      );
    const updatedList = [
      ...projectFeeAndExpenseState.filteredFeeAndExpenseList,
    ];
    const originalUpdatedList = [
      ...projectFeeAndExpenseState.originalFeeAndExpenseList,
    ];
    originalUpdatedList[originalListIndex].isChecked = checked;
    updatedList[filterListIndex].isChecked = checked;
    const totalBillingAmount = getTotalSelectedAmount(
      updatedList,
      "totalAmount",
    );
    const totalTaxAmount = getTotalSelectedAmount(updatedList, "tax");

    setProjectFeeAndExpenseState((prevState) => ({
      ...prevState,
      filteredFeeAndExpenseList: updatedList,
      originalFeeAndExpenseList: originalUpdatedList,
      totalBillingAmount: totalBillingAmount,
      totalTax: totalTaxAmount,
    }));
  };

  const columns: Column[] = [
    {
      id: ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseName,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.feeExpenseName,
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          onClick={() =>
            handleOnViewClick(row as unknown as ProjectFeesAndExpenseRow)
          }
        >
          <MuiBox className="link">
            <MuiEllipseLabel
              text={
                row[ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseName]
              }
              labelTypoVariant="h3"
              placement="bottom-start"
              pointerCursor
            />
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectTransactionDate,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.transactionDate,
      ),
      isSortVisible: true,
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectType,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.itemType,
      ),
      minWidth: 200,
      isSortVisible: true,
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectCategory,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.itemCategory,
      ),
      minWidth: 200,
      isSortVisible: true,
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseType,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.feeExpenseType,
      ),
      isSortVisible: true,
    },

    {
      id: ProjectFeesAndExpensesColumnKey.projectStatusId,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.itemStatus,
      ),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge
          status={row.statusId}
          label={getStatusValueByKeyOfDropdown(row.statusId)}
        />
      ),
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectUnitRate,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.unitRate,
      ),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProjectFeesAndExpensesColumnKey.projectUnitRate],
          row.currencyName,
        );
      },
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectQuantity,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.quantity,
      ),
      isSortVisible: true,
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseAmount,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.amount,
      ),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseAmount],
          row.currencyName,
        );
      },
    },

    {
      id: ProjectFeesAndExpensesColumnKey.projectBillingAmount,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.billingAmount,
      ),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProjectFeesAndExpensesColumnKey.projectBillingAmount],
          billingCurrencyName,
        );
      },
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectTaxAmount,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.taxAmount,
      ),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProjectFeesAndExpensesColumnKey.projectTaxAmount],
          billingCurrencyName,
        );
      },
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectTotalInvoiceAmount,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.totalInvoiceAmount,
      ),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProjectFeesAndExpensesColumnKey.projectTotalInvoiceAmount],
          billingCurrencyName,
        );
      },
    },
    {
      id: ProjectFeesAndExpensesColumnKey.projectComment,
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.comments,
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          {isNotEmpty(row?.[ProjectFeesAndExpensesColumnKey.projectComment]) ? (
            <MuiNotesToolTip
              message={row?.[ProjectFeesAndExpensesColumnKey.projectComment]}
            />
          ) : (
            EMPTY_VALUE_DASH
          )}
        </MuiBox>
      ),
    },
  ];

  if (isEditWithPermission && isEditProject) {
    columns.unshift({
      id: ProjectFeesAndExpensesColumnKey.projectIsChecked,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          hideLabel={true}
          checkboxLabel={`${commonARIALabel?.selectAll}`}
          onCheckedChange={(checked: boolean) => onChangeSelectAll(checked)}
        />
      ),
      render: (row: RowProps) => {
        return (
          <MuiCheckbox
            disabled={row.statusId !== STATUS_BADGE_TYPE.unBilled}
            checked={row.isChecked ?? false}
            hideLabel={true}
            checkboxLabel={replaceHashWithValue(
              commonARIALabel.selectRowItemName,
              `${row.name}`,
            )}
            id={`${pageId}-${row.id}`}
            onCheckedChange={(checked: boolean) => {
              onChangeSelect(checked, row as ProjectFeesAndExpenseRow);
            }}
          />
        );
      },
    });
  }

  if (!isViewOnly) {
    columns.push({
      id: "action",
      label: getProjectFeeAndExpenseLabel(
        projectFeesAndExpensesSiteCoreKeys.itemAction,
      ),
      render: (row: RowProps) => {
        if (
          row.statusId === STATUS_BADGE_TYPE.unBilled &&
          row.typeId !== ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_FEE
        ) {
          const actions = [
            {
              permissionKey: MODULE_PERMISSIONS.PROJECT_EDIT_FEES_AND_EXPENSES,
              create: () => ({
                onClick: () =>
                  onActionClick(row as ProjectExpenseData, TableActions.edit),
                dataTestId: "edit-program-fee-and-expense",
                label: commonLabels.edit,
              }),
            },
            {
              permissionKey:
                MODULE_PERMISSIONS.PROJECT_DELETE_FEES_AND_EXPENSES,
              create: () => ({
                onClick: () =>
                  onActionClick(row as ProjectExpenseData, TableActions.delete),
                dataTestId: "delete-program-fee-and-expense",
                label: commonLabels.delete,
              }),
            },
          ];

          const menuItems = buildActionMenuItems(
            row,
            actions,
            hasModulePermission,
          );

          return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
        }
        return <></>;
      },
    });
  }

  const tableHeaderForm: React.JSX.Element[] = [
    ...(isEditWithPermission && isEditProject ? [<></>] : []),
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseName}
      control={control}
      placeholder={commonLabels.search}
      key="1"
      requiredARIALabel={commonARIALabel.required}
    />,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={ProjectFeesAndExpensesColumnKey.projectTransactionDate}
      key={ProjectFeesAndExpensesColumnKey.projectTransactionDate}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,
    <ProjectSearchDropdownControl
      name={ProjectFeesAndExpensesColumnKey.projectType}
      list={projectFeeAndExpenseState.typeIdOptions}
      control={control}
      placeholder={commonLabels.search}
      requiredARIALabel={commonARIALabel.required}
      key="2"
    />,
    <ProjectSearchDropdownControl
      name={ProjectFeesAndExpensesColumnKey.projectCategory}
      list={projectFeeAndExpenseState.categoryOptions}
      control={control}
      placeholder={commonLabels.search}
      requiredARIALabel={commonARIALabel.required}
      key="3"
    />,
    <ProjectSearchDropdownControl
      name={ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseType}
      list={projectFeeAndExpenseState.feeOrExpenseTypeOptions}
      control={control}
      placeholder={commonLabels.search}
      requiredARIALabel={commonARIALabel.required}
      key="4"
    />,
    <ProjectSearchDropdownControl
      name={ProjectFeesAndExpensesColumnKey.projectStatusId}
      list={fetchDropdownValues(
        projectFeesAndExpensesSiteCoreKeys.feesAndExpenses,
        projectFeesAndExpensesSiteCoreKeys.itemStatusList,
        false,
      )}
      control={control}
      placeholder={commonLabels.search}
      requiredARIALabel={commonARIALabel.required}
      key="5"
    />,
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectUnitRate}
      control={control}
      placeholder={commonLabels.search}
      key="6"
      requiredARIALabel={commonARIALabel.required}
    />,
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectQuantity}
      control={control}
      placeholder={commonLabels.search}
      key="7"
      requiredARIALabel={commonARIALabel.required}
    />,
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectFeesOrExpenseAmount}
      control={control}
      placeholder={commonLabels.search}
      key="8"
      requiredARIALabel={commonARIALabel.required}
    />,
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectBillingAmount}
      control={control}
      placeholder={commonLabels.search}
      key="9"
      requiredARIALabel={commonARIALabel.required}
    />,
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectTaxAmount}
      control={control}
      placeholder={commonLabels.search}
      key="10"
      requiredARIALabel={commonARIALabel.required}
    />,
    <ProjectSearchFormControl
      name={ProjectFeesAndExpensesColumnKey.projectTotalInvoiceAmount}
      control={control}
      placeholder={commonLabels.search}
      key="11"
      requiredARIALabel={commonARIALabel.required}
    />,
    <></>,
    <></>,
  ];

  const addButtonLabel = getProjectFeeAndExpenseLabel(
    projectFeesAndExpensesSiteCoreKeys.addBillingItemBtn,
  );

  const handleCloseDossierError = () => {
    setShowDossierError(false);
  };
  const handleProjectRaiseInvoice = () => {
    if (
      isClientTypeNCBP &&
      (isEmpty(keyDetails?.dossierNumber) ||
        isEmpty(keyDetails?.actualClient?.id))
    ) {
      setShowDossierError(true);
      return;
    }
    setShowProjectRaiseInvoiceModal(true);
  };

  function handleViewInvoiceHistory(): void {
    setShowSuccessModal(false);
    setShowProjectRaiseInvoiceModal(false);
    setShowInvoiceHistoryModal(true);
  }

  const { downloadInvoiceExcel } = useDownloadInvoiceToExcel();
  function handleDownloadInvoice(selectedInvoice: string): void {
    downloadInvoiceExcel(selectedInvoice, DELIVERABLE_FRAMEWORK_TYPE.PROJECT);
  }

  const onProjectRaiseInvoiceValuesChange = (params: CaptureValuesParams) => {
    setSelectedPONumber(params.poNumber);
    setProjectInvoiceDate(params.projectInvoiceDate);
    setPaymentTerms(params.paymentTerms);
    setLocalInvoiceNumber(params.localInvoiceNumber);
  };

  return (
    <>
      <MuiBox
        className="fees-and-expense-container"
        data-testid="fees-and-expense-list"
      >
        {isViewOnly ? (
          <MuiDoubleLabelHorizontal
            label={getProjectFeeAndExpenseLabel(
              projectFeesAndExpensesSiteCoreKeys.invoiceAmount,
            )}
            value={getTotalProjectInvoiceAmount()}
            className="invoice-amount"
          />
        ) : (
          <MuiBox className="add-fee-expense-btns">
            {isAddButtonVisible ? (
              <ButtonWithIcon
                buttonText={addButtonLabel}
                onActionClick={handleAddFeesAndExpense}
              />
            ) : null}
            {Boolean(projectFeeAndExpenseState.totalBillingAmount) && (
              <PermissionWrapper
                permission={MODULE_PERMISSIONS.PROGRAM_ADD_INVOICE_GENERATION}
              >
                <MuiBox className="divider"></MuiBox>
                <MuiButtonPrimary onClick={handleProjectRaiseInvoice}>
                  {projectRaiseInvoiceLabel}
                </MuiButtonPrimary>
              </PermissionWrapper>
            )}
          </MuiBox>
        )}
      </MuiBox>
      <MuiTable
        ref={projectFeeAndExpenseTableRef}
        pageId={pageId}
        isLastColumnSticky={!isViewOnly}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={
          projectFeeAndExpenseState.filteredFeeAndExpenseList as unknown as Record<
            string,
            ReactNode
          >[]
        }
        noDataInApi={
          projectFeeAndExpenseState.originalFeeAndExpenseList.length === 0
        }
        emptyMessage={emptyMessageLabel}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
      {!isViewOnly && showProjectRaiseInvoiceModal && (
        <ProjectRaiseInvoice
          onClose={() => setShowProjectRaiseInvoiceModal(false)}
          modalTitle={fetchComponentTitle(
            projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
          )}
          open={showProjectRaiseInvoiceModal}
          invoiceFeesAndExpense={projectFeeAndExpenseState.filteredFeeAndExpenseList.filter(
            (item) => item.isChecked,
          )}
          totalBillingAmount={parseFloat(
            (
              projectFeeAndExpenseState.totalBillingAmount -
              projectFeeAndExpenseState.totalTax
            ).toFixed(2),
          )}
          totalTaxAmount={parseFloat(
            projectFeeAndExpenseState.totalTax.toFixed(2),
          )}
          totalInvoiceAmount={parseFloat(
            projectFeeAndExpenseState.totalBillingAmount.toFixed(2),
          )}
          totalInvoiceCurrency={billingCurrencyName}
          onPrimaryButtonClick={handlePrimaryButtonClick}
          callBackValues={onProjectRaiseInvoiceValuesChange}
          poNumber={poNumber}
          isProjectRaiseInvoiceError={isProjectRaiseInvoiceError}
          projectRaiseInvoiceError={projectRaiseInvoiceError}
        />
      )}

      {!isViewOnly && showInvoiceHistoryModal && (
        <MuiModal
          hasLargeContent
          open={showInvoiceHistoryModal}
          onClose={() => setShowInvoiceHistoryModal(false)}
          title={fetchComponentTitle(
            projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
          )}
          primaryButtonTitle={commonLabels.ok}
          onPrimaryButtonClick={handleInvoiceButtonClick}
        >
          <ProjectInvoiceHistory projectId={projectId} />
        </MuiModal>
      )}

      {showConfirmationModal && (
        <MuiModal
          open={showConfirmationModal}
          onClose={handleCancel}
          title={commonLabels.confirmation}
          primaryButtonTitle={commonLabels.yesProceed}
          onPrimaryButtonClick={handleConfirm}
          secondaryButtonTitle={commonLabels.no}
          onSecondaryButtonClick={handleCancel}
        >
          <MuiTypography>
            {fetchLabel(
              projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
              projectRaiseInvoiceSiteCoreKeys.areYouSureYouWantToRaiseTheInvoice,
            )}
          </MuiTypography>
        </MuiModal>
      )}

      {showSuccessModal && (
        <MuiModal
          open={showSuccessModal}
          onClose={handleSuccessClose}
          title={fetchLabel(
            projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
            projectRaiseInvoiceSiteCoreKeys.successPopupHeader,
          )}
          primaryButtonTitle={commonLabels.ok}
          onPrimaryButtonClick={handleSuccessClose}
          tertiaryButtonTitle={
            hasModulePermission(MODULE_PERMISSIONS.PROGRAM_VIEW_INVOICE_HISTORY)
              ? fetchLabel(
                  projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
                  projectRaiseInvoiceSiteCoreKeys.viewInvoiceHistoryLinkText,
                )
              : ""
          }
          onTertiaryButtonClick={() => handleViewInvoiceHistory()}
          className="view-invoice-history-btn"
          leftActionButtonClick={() => handleDownloadInvoice(projectInvoiceId)}
          leftActionButtonTitle={commonLabels.downloadInvoice}
          leftActionButtonIcon={<DownloadIcon />}
        >
          <MuiTypography
            dangerouslySetInnerHTML={{
              __html: replaceHashWithValue(
                fetchLabel(
                  projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
                  projectRaiseInvoiceSiteCoreKeys.successPopupDescriptionText,
                ),
                {
                  INVOICENUMBER: projectInvoiceNumber,
                  TOTALINVOICEAMONT: `${billingCurrencyName} ${projectFeeAndExpenseState.totalBillingAmount.toFixed(2)}`,
                },
              ),
            }}
          ></MuiTypography>
        </MuiModal>
      )}

      {showProjectFeeViewModal && (
        <ViewProjectFeeModal
          open={showProjectFeeViewModal}
          onClose={() => setShowProjectFeeViewModal(false)}
          projectId={projectId}
        />
      )}

      {showViewFeesAndExpenseModal && (
        <ViewProjectFeeExpenseModal
          billingCurrencyName={billingCurrencyName}
          open={showViewFeesAndExpenseModal}
          projectExpenseId={selectedProjectExpenseId}
          onClose={() => setShowViewFeesAndExpenseModal(false)}
        />
      )}
      {showDossierError && (
        <WarningConfirmationModal
          show={showDossierError}
          primaryButtonTitle={commonLabels.ok}
          showSecondaryButton={false}
          onClose={handleCloseDossierError}
          onPrimaryButtonClick={handleCloseDossierError}
          labelContent={getProjectFeeAndExpenseLabel(
            projectFeesAndExpensesSiteCoreKeys.dossierNumberActualClientNotAvailableMessage,
          )}
        />
      )}
    </>
  );
};
